import type { TenantSettings } from "@/types/tenant";

/**
 * Optional site modules a platform admin can toggle per tenant.
 * Pages, menus, media, and settings are core and always available.
 * Legacy tenants without a module list retain core modules; newer modules opt in.
 */
export const OPTIONAL_MODULES = [
  { key: "blog", label: "Blog (incl. WordPress sync)" },
  { key: "rss", label: "RSS blog sources" },
  { key: "podcasts", label: "Podcasts" },
  { key: "faq", label: "FAQ" },
  { key: "staff", label: "Staff" },
  { key: "gallery", label: "Gallery" },
  { key: "contact", label: "Contact forms" },
  { key: "forms", label: "Forms builder" },
  { key: "testimonials", label: "Testimonials & reviews" },
  { key: "locations", label: "Locations" },
  { key: "events", label: "Events" },
  { key: "products", label: "Product catalog" },
  { key: "product-matching", label: "Product finder" },
  { key: "therapists", label: "Therapist directory" },
  { key: "therapist-matching", label: "Therapist matching" },
  { key: "restaurant", label: "Restaurant / dining menu" },
  { key: "radio", label: "Radio shows & schedule" },
  { key: "booking", label: "Booking / scheduler" },
  { key: "music", label: "Music / Discography" },
  { key: "services", label: "Services catalog" },
  { key: "qr-codes", label: "QR codes" },
  { key: "club", label: "Club / membership" },
] as const;

export type ModuleKey = (typeof OPTIONAL_MODULES)[number]["key"];

export function updateEnabledModules(
  current: readonly string[],
  module: ModuleKey,
  enabled: boolean,
): ModuleKey[] {
  const next = new Set(current);
  if (enabled) {
    next.add(module);
    if (module === "product-matching") next.add("products");
    if (module === "therapist-matching") next.add("therapists");
    if (module === "rss") next.add("blog");
  } else {
    next.delete(module);
    if (module === "products") next.delete("product-matching");
    if (module === "therapists") next.delete("therapist-matching");
    if (module === "blog") next.delete("rss");
  }
  return OPTIONAL_MODULES.map((value) => value.key).filter((key) => next.has(key));
}

/** Public page types owned by a module; used to gate rendering. */
export const PAGE_TYPE_MODULE: Record<string, ModuleKey> = {
  blog: "blog",
  faq: "faq",
  staff: "staff",
  gallery: "gallery",
  contact: "contact",
  locations: "locations",
  events: "events",
  menu: "restaurant",
};

export function isModuleEnabled(
  settings: TenantSettings | undefined,
  module: ModuleKey,
): boolean {
  const modules = settings?.modules;
  if (!Array.isArray(modules)) {
    return module !== "products" && module !== "product-matching" && module !== "therapists" && module !== "therapist-matching" && module !== "rss" && module !== "podcasts" && module !== "radio" && module !== "music" && module !== "booking" && module !== "services" && module !== "qr-codes" && module !== "club";
  }
  if (module === "product-matching") {
    return modules.includes(module) && modules.includes("products");
  }
  if (module === "therapist-matching") {
    return modules.includes(module) && modules.includes("therapists");
  }
  if (module === "rss") {
    return modules.includes(module) && modules.includes("blog");
  }
  return modules.includes(module);
}

export function getBlogSource(
  settings: TenantSettings | undefined,
): "internal" | "wordpress" {
  const source = settings?.blogSource;
  return source === "wordpress" ? "wordpress" : "internal";
}
